import React, { useState, useEffect } from 'react';
import InboxPopover from './InboxPopover';
import {
  Box,
  AppBar,
  Toolbar,
  IconButton,
  Typography,
  Avatar,
  Menu,
  MenuItem,
  Divider,
  alpha,
  Button,
  Popover,
  List,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  useMediaQuery,
  useTheme,
  Drawer,
  Tooltip,
  Switch,
  ListItem
} from '@mui/material';
import {
  Home as HomeIcon,
  DirectionsCar as CarIcon,
  People as PeopleIcon,
  ReceiptLong as SalesIcon,
  Settings as SettingsIcon,
  Logout as LogoutIcon,
  KeyboardArrowDown as ArrowDownIcon,
  Menu as MenuIcon,
  Close as CloseIcon,
  Calculate as CalculateIcon,
  AttachMoney as AttachMoneyIcon,
  CalendarMonth as CalendarIcon,
  Construction as ToolsIcon,
  Psychology as IntelligenceIcon,
  Search as SearchIcon,
  AutoAwesome as AIIcon,
  Build as BuildIcon,
  CloudUpload as MigrationIcon,
  Inventory as StockIcon,
  BusinessCenter as OperationsIcon,
  AccountBalance as FinanceIcon,
  TrendingUp as MarketingIcon,
  AdminPanelSettings as SystemIcon,
  ReceiptLong,
  BarChart as ReportsIcon,
  Campaign as AdvertisingIcon,
  Assignment as TaskIcon,
  Person as PersonIcon,
  RocketLaunch as GettingStartedIcon,
  Help as HelpIcon,
  Announcement as AnnouncementIcon,
  CreditCard as SubscriptionIcon,
  Description as LicenceIcon,
  AccountBalance as BankIcon,
  ContactPhone as ContactIcon,
  Backup as BackupIcon,
  Add as AddIcon,
  Handshake as HandshakeIcon,
  MonetizationOn as MoneyIcon,
  ShoppingCart as PurchaseIcon,
  RequestQuote as QuoteIcon,
  PersonAdd as PersonAddIcon,
  LightMode as LightModeIcon,
  DarkMode as DarkModeIcon,
  ViewSidebar as SidebarIcon,
  ViewDay as TopNavIcon
} from '@mui/icons-material';
import { Outlet, useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../../hooks/useAuth';
import { useThemeMode } from '../../contexts/ThemeContext';
import LocationSelector from '../LocationSelector';
import { useFilteredNavigation } from '../../hooks/useFilteredNavigation';
import GlobalSearchModal from '../GlobalSearchModal';
import { NavigationProvider, AppBreadcrumbs, useNavigationSafe } from '../../contexts/NavigationContext';
import AppleStyleDealFlowEnhanced from '../AppleStyleDealFlowEnhanced';
import AddCustomerModal from '../customers/AddCustomerModal';
import QuickAddExpenseModal from '../QuickAddExpenseModal';
import ScrollToTop from '../ScrollToTop';
import ServiceFlowDialog from '../ServiceFlowDialog';

export default function TopNavLayout() {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const isVerySmall = useMediaQuery(theme.breakpoints.down('sm'));
  const isExtraSmall = useMediaQuery('(max-width:400px)');
  const navigate = useNavigate();
  const location = useLocation();
  const isFullBleed = ['/ai', '/inbox'].includes(location.pathname);
  const { user, currentOrganization, signOut } = useAuth();
  const { mode: themeMode, toggleTheme } = useThemeMode();
  const { createNavigationHandler } = useNavigationSafe();
  const [navigationLayout, setNavigationLayout] = useState(
    localStorage.getItem('navigationLayout') || 'top'
  );

  const [profileAnchorEl, setProfileAnchorEl] = useState<null | HTMLElement>(null);
  const [mobileDrawerOpen, setMobileDrawerOpen] = useState(false);
  const [menuAnchorEls, setMenuAnchorEls] = useState<Record<string, HTMLElement | null>>({});
  const [quickAddAnchorEl, setQuickAddAnchorEl] = useState<null | HTMLElement>(null);
  const [quickExpenseOpen, setQuickExpenseOpen] = useState(false);
  const [searchModalOpen, setSearchModalOpen] = useState(false);
  // dealFlowOpen state removed — sale creation routes to /sales/new
  const [dealFlowMode, setDealFlowMode] = useState<'sale' | 'quote'>('quote');
  const [addCustomerModalOpen, setAddCustomerModalOpen] = useState(false);
  const [serviceFlowOpen, setServiceFlowOpen] = useState(false);
  // proposalFlowOpen / saleFlowOpen removed — Quick Add menu navigates to /sales/new?mode=...

  // Global keyboard shortcuts
  useEffect(() => {
    const handleKeyDown = (event: KeyboardEvent) => {
      // Search shortcut (Cmd+K or Ctrl+K)
      if ((event.metaKey || event.ctrlKey) && event.key === 'k') {
        event.preventDefault();
        setSearchModalOpen(true);
      }
      // Quick Add shortcut (Cmd+N or Ctrl+N)
      if ((event.metaKey || event.ctrlKey) && event.key === 'n') {
        event.preventDefault();
        const btn = document.getElementById('quick-add-button-topnav');
        if (btn) btn.click();
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, []);

  // Close all menus when route changes
  useEffect(() => {
    setMenuAnchorEls({});
    setMobileDrawerOpen(false);
  }, [location.pathname]);

  const handleProfileMenuOpen = (event: React.MouseEvent<HTMLElement>) => {
    setProfileAnchorEl(event.currentTarget);
  };

  const handleProfileMenuClose = () => {
    setProfileAnchorEl(null);
  };

  const handleLogout = async () => {
    handleProfileMenuClose();
    await signOut();
    navigate('/login');
  };

  const handleLayoutToggle = () => {
    const newLayout = navigationLayout === 'side' ? 'top' : 'side';
    setNavigationLayout(newLayout);
    localStorage.setItem('navigationLayout', newLayout);
    // Dispatch event to trigger layout change
    window.dispatchEvent(new CustomEvent('navigationLayoutChange', { detail: { layout: newLayout } }));
  };

  const handleNavigation = (path: string) => {
    // Close all menus first to prevent race conditions
    setMenuAnchorEls({});
    setMobileDrawerOpen(false);
    // Small delay to ensure state updates before navigation
    setTimeout(() => navigate(path), 0);
  };

  // Create navigation handler with middle-click support
  const handleNavigationClick = (path: string) => {
    const handler = createNavigationHandler(path);
    return (e: React.MouseEvent) => {
      // Close menus
      setMenuAnchorEls({});
      setMobileDrawerOpen(false);
      // Call the navigation handler
      handler(e);
    };
  };

  const handleMenuOpen = (menuId: string, event: React.MouseEvent<HTMLElement>) => {
    // Close other menus first to prevent multiple popovers
    setMenuAnchorEls({
      [menuId]: event.currentTarget
    });
  };

  const handleMenuClose = (menuId: string) => {
    setMenuAnchorEls(prev => {
      // Defensive check - if menu is already cleared, don't update
      if (!prev[menuId]) return prev;
      const newState = { ...prev };
      delete newState[menuId];
      return newState;
    });
  };

  const handleQuickAddMenuOpen = (event: React.MouseEvent<HTMLElement>) => {
    setQuickAddAnchorEl(event.currentTarget);
  };

  const handleQuickAddMenuClose = () => {
    setQuickAddAnchorEl(null);
  };

  const handleQuickAddAction = (path: string) => {
    handleQuickAddMenuClose();

    // Special handling for tasks - navigate to calendar with dialog open
    if (path === '/tasks') {
      navigate('/calendar?openAdd=true');
      return;
    }

    // Special handling for customers - open AddCustomerModal
    if (path === '/customers/add') {
      setAddCustomerModalOpen(true);
      return;
    }

    // Quick-add Proposal → wizard route in quote mode
    if (path === '/quote') {
      navigate('/sales/new?mode=quote');
      return;
    }

    // Quick-add Sale → wizard route in sale mode
    if (path === '/sales/add') {
      navigate('/sales/new?mode=sale');
      return;
    }

    // TEMPORARILY HIDDEN: Workshop
    // Special handling for workshop - open service flow dialog
    // if (path === '/workshop') {
    //   setServiceFlowOpen(true);
    //   return;
    // }

    navigate(path);
    setMobileDrawerOpen(false);
  };

  // Use permission-filtered menu structure
  const { filteredNavigation: menuStructure } = useFilteredNavigation();

  // Mobile drawer content
  const mobileDrawerContent = (
    <Box sx={{ 
      width: isExtraSmall ? 260 : 280, 
      height: '100%', 
      display: 'flex', 
      flexDirection: 'column',
      maxWidth: '85vw' // Ensure it doesn't exceed viewport width
    }}>
      {/* Header */}
      <Box sx={{ p: 2, borderBottom: 3, borderColor: 'divider' }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
          <Typography variant="h6" sx={{ fontWeight: 600 }}>
            Menu
          </Typography>
          <IconButton onClick={() => setMobileDrawerOpen(false)} size="small">
            <CloseIcon />
          </IconButton>
        </Box>
        {/* User Info */}
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, p: 1.5, bgcolor: 'background.default', borderRadius: 1 }}>
          <Avatar
            sx={{
              width: 40,
              height: 40,
              bgcolor: 'primary.main',
            }}
          >
            {user?.email?.[0]?.toUpperCase()}
          </Avatar>
          <Box sx={{ flex: 1, minWidth: 0 }}>
            <Typography variant="body2" sx={{ fontWeight: 500, overflow: 'hidden', textOverflow: 'ellipsis' }}>
              {user?.email}
            </Typography>
            <Typography variant="caption" sx={{ color: 'text.secondary', overflow: 'hidden', textOverflow: 'ellipsis', display: 'block' }}>
              {currentOrganization?.name}
            </Typography>
          </Box>
        </Box>
      </Box>

      {/* Navigation List */}
      <Box sx={{ flex: 1, overflowY: 'auto', p: { xs: 0.5, sm: 1 } }}>
        <List dense={isExtraSmall}>
        {menuStructure.map((item) => {
          if (item.standalone && item.path) {
            return (
              <ListItemButton
                key={item.text}
                onClick={handleNavigationClick(item.path)}
                onMouseDown={handleNavigationClick(item.path)}
                selected={location.pathname === item.path}
              >
                <ListItemIcon>{item.icon}</ListItemIcon>
                <ListItemText
                  primary={item.text}
                  primaryTypographyProps={{
                    fontWeight: location.pathname === item.path ? 600 : 500
                  }}
                />
              </ListItemButton>
            );
          }
          if (item.children) {
            return (
              <Box key={item.text}>
                <Typography variant="overline" sx={{ px: 2, py: 1, display: 'block', color: 'text.secondary' }}>
                  {item.text}
                </Typography>
                {item.children.map(child => (
                  <ListItemButton
                    key={child.text}
                    onClick={handleNavigationClick(child.path)}
                    onMouseDown={handleNavigationClick(child.path)}
                    selected={location.pathname === child.path}
                    sx={{ pl: 4 }}
                  >
                    <ListItemIcon>{child.icon}</ListItemIcon>
                    <ListItemText
                      primary={child.text}
                      primaryTypographyProps={{
                        fontWeight: location.pathname === child.path ? 600 : 500
                      }}
                    />
                  </ListItemButton>
                ))}
              </Box>
            );
          }
          return null;
        })}
        </List>
      </Box>

      {/* Footer with profile options */}
      <Box sx={{ borderTop: 3, borderColor: 'divider', p: 1 }}>
        <List dense>
          <ListItemButton onClick={handleNavigationClick('/profile')} onMouseDown={handleNavigationClick('/profile')}>
            <ListItemIcon><PersonIcon fontSize="small" /></ListItemIcon>
            <ListItemText primary="Your Profile" primaryTypographyProps={{ fontWeight: 500 }} />
          </ListItemButton>
          <ListItemButton onClick={handleNavigationClick('/settings')} onMouseDown={handleNavigationClick('/settings')}>
            <ListItemIcon><SettingsIcon fontSize="small" /></ListItemIcon>
            <ListItemText primary="Settings" primaryTypographyProps={{ fontWeight: 500 }} />
          </ListItemButton>
          <ListItemButton onClick={handleNavigationClick('/help')} onMouseDown={handleNavigationClick('/help')}>
            <ListItemIcon><HelpIcon fontSize="small" /></ListItemIcon>
            <ListItemText primary="Help & Support" primaryTypographyProps={{ fontWeight: 500 }} />
          </ListItemButton>
          <Divider sx={{ my: 0.5 }} />
          <ListItemButton onClick={handleLogout}>
            <ListItemIcon><LogoutIcon fontSize="small" /></ListItemIcon>
            <ListItemText primary="Sign out" primaryTypographyProps={{ fontWeight: 500 }} />
          </ListItemButton>
        </List>
      </Box>
    </Box>
  );

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', height: '100vh' }}>
      <ScrollToTop />
      {/* Top Navigation Bar */}
      <AppBar
        position="fixed"
        sx={{
          bgcolor: 'background.paper',
          borderBottom: 3,
          borderColor: 'divider',
          boxShadow: 'none',
        }}
      >
        <Toolbar sx={{ 
          px: { xs: 0.5, sm: 1, md: 2, lg: 3 }, 
          minHeight: { xs: 56, sm: 64 }, 
          justifyContent: 'space-between', 
          alignItems: 'center',
          gap: { xs: 0.5, sm: 1 }
        }}>
          {/* Logo */}
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              cursor: 'pointer',
              flexShrink: 0,
              '&:hover': { opacity: 0.8 }
            }}
            onClick={() => navigate('/')}
          >
            <img
              src="/DealerOS - Icon.svg"
              alt="DealerOS Icon"
              style={{ 
                height: isExtraSmall ? '24px' : '28px', 
                width: isExtraSmall ? '24px' : '28px', 
                marginRight: isVerySmall ? '4px' : '8px' 
              }}
            />
            <Typography
              variant="h6"
              sx={{
                fontWeight: 600,
                color: 'primary.main',
                display: { xs: isExtraSmall ? 'none' : 'block', sm: 'block' },
                fontSize: { xs: '0.9rem', sm: '1.1rem', md: '1.25rem' }
              }}
            >
              Dealer<Box component="span" sx={{ fontWeight: 800 }}>OS</Box>
            </Typography>
          </Box>

          {/* Desktop Navigation - Centered */}
          {!isMobile ? (
            <Box sx={{
              display: 'flex',
              gap: { md: 1.5, lg: 2 }, // More spacing like collapsed sidebar
              justifyContent: 'center',
              alignItems: 'center',
              flexGrow: 1,
              overflow: 'hidden'
            }}>
              {menuStructure.map((item) => {
                if (item.standalone && item.path) {
                  const isActive = location.pathname === item.path || 
                    (item.path === '/' && location.pathname === '/');
                  return (
                    <Button
                      key={item.text}
                      onClick={handleNavigationClick(item.path)}
                      onMouseDown={handleNavigationClick(item.path)}
                      startIcon={React.cloneElement(item.icon as React.ReactElement, {
                        sx: { fontSize: { md: '1.5rem', lg: '1.75rem' } } // Larger icons like collapsed sidebar
                      })}
                      disableRipple
                      sx={{
                        color: isActive ? 'primary.main' : 'text.primary',
                        bgcolor: isActive ? alpha('#10b981', 0.1) : 'transparent',
                        fontWeight: isActive ? 600 : 500,
                        whiteSpace: 'nowrap',
                        px: { md: 1.5, lg: 2.5 }, // More padding like collapsed sidebar
                        py: { md: 1, lg: 1.25 },
                        fontSize: { md: '1rem', lg: '1.1rem' }, // Larger font
                        minWidth: 'auto',
                        borderRadius: 2,
                        border: 'none',
                        outline: 'none',
                        boxShadow: 'none !important',
                        '& .MuiButton-startIcon': {
                          marginRight: { md: 1, lg: 1.5 }, // More spacing between icon and text
                        },
                        '&:focus': {
                          outline: 'none',
                          border: 'none',
                          boxShadow: 'none',
                        },
                        '&:focus-visible': {
                          outline: 'none',
                          border: 'none',
                          boxShadow: 'none',
                        },
                        '&:hover': {
                          bgcolor: alpha('#10b981', 0.05),
                        },
                      }}
                    >
                      {item.text}
                    </Button>
                  );
                }
                if (item.children) {
                  const hasActiveChild = item.children.some(child => 
                    location.pathname === child.path
                  );
                  return (
                    <Box key={item.text}>
                      <Button
                        onClick={(e) => handleMenuOpen(item.text, e)}
                        startIcon={item.icon ? React.cloneElement(item.icon as React.ReactElement, {
                          sx: { fontSize: { md: '1.5rem', lg: '1.75rem' } }
                        }) : undefined}
                        endIcon={<ArrowDownIcon sx={{ fontSize: { md: '1.25rem', lg: '1.5rem' } }} />}
                        disableRipple
                        sx={{
                          color: hasActiveChild ? 'primary.main' : 'text.primary',
                          bgcolor: hasActiveChild ? alpha('#10b981', 0.05) : 'transparent',
                          fontWeight: hasActiveChild ? 600 : 500,
                          whiteSpace: 'nowrap',
                          px: { md: 1.5, lg: 2.5 }, // More padding like collapsed sidebar
                          py: { md: 1, lg: 1.25 },
                          fontSize: { md: '1rem', lg: '1.1rem' }, // Larger font
                          minWidth: 'auto',
                          borderRadius: 2,
                          border: 'none',
                          outline: 'none',
                          boxShadow: 'none !important',
                          '& .MuiButton-startIcon': {
                            marginRight: { md: 1, lg: 1.5 },
                          },
                          '&:focus': {
                            outline: 'none',
                            border: 'none',
                            boxShadow: 'none',
                          },
                          '&:focus-visible': {
                            outline: 'none',
                            border: 'none',
                            boxShadow: 'none',
                          },
                          '&:hover': {
                            bgcolor: alpha('#10b981', 0.05),
                          },
                        }}
                      >
                        {item.text}
                      </Button>
                      <Popover
                        open={Boolean(menuAnchorEls[item.text])}
                        anchorEl={menuAnchorEls[item.text]}
                        onClose={() => handleMenuClose(item.text)}
                        transitionDuration={0}
                        anchorOrigin={{
                          vertical: 'bottom',
                          horizontal: 'left',
                        }}
                        transformOrigin={{
                          vertical: 'top',
                          horizontal: 'left',
                        }}
                      >
                        <List sx={{ minWidth: 200, py: 0.5 }}>
                          {item.children.map((child, index, array) => {
                            const isActive = location.pathname === child.path;
                            return (
                              <React.Fragment key={child.text}>
                                <ListItemButton
                                  onClick={handleNavigationClick(child.path)}
                                  onMouseDown={handleNavigationClick(child.path)}
                                  selected={isActive}
                                  sx={{
                                    py: 1.5,
                                    '&.Mui-selected': {
                                      bgcolor: alpha('#10b981', 0.1),
                                    }
                                  }}
                                >
                                  <ListItemIcon>{child.icon}</ListItemIcon>
                                  <ListItemText
                                    primary={child.text}
                                    primaryTypographyProps={{
                                      fontWeight: isActive ? 600 : 500
                                    }}
                                  />
                                </ListItemButton>
                                {index < array.length - 1 && <Divider sx={{ opacity: 0.5 }} />}
                              </React.Fragment>
                            );
                          })}
                        </List>
                      </Popover>
                    </Box>
                  );
                }
                return null;
              })}
            </Box>
          ) : (
            <Box sx={{ flexGrow: 1 }} />
          )}

          {/* Right side items */}
          <Box sx={{
            display: 'flex',
            alignItems: 'center',
            gap: { xs: 0.25, sm: 0.5, md: 1, lg: 2 },
            justifyContent: 'flex-end',
            flexShrink: 0,
            minWidth: 0 // Allow shrinking if needed
          }}>
            {/* Quick Add Button */}
            <Tooltip title="Quick Add (⌘N)">
              <IconButton
                id="quick-add-button-topnav"
                onClick={handleQuickAddMenuOpen}
                sx={{
                  color: 'primary.main',
                  bgcolor: alpha('#10b981', 0.1),
                  '&:hover': {
                    bgcolor: alpha('#10b981', 0.2),
                  },
                }}
              >
                <AddIcon />
              </IconButton>
            </Tooltip>

            {/* Inbox popover */}
            <InboxPopover />

            {/* TEMPORARILY HIDDEN: AI Assistant Icon */}
            {/* <Tooltip title="AI Assistant">
              <IconButton
                onClick={() => navigate('/ai')}
                sx={{
                  color: location.pathname === '/ai' ? 'primary.main' : 'text.primary',
                  '&:hover': {
                    bgcolor: theme.palette.mode === 'dark' ? alpha('#fff', 0.12) : alpha('#000', 0.08),
                  },
                }}
              >
                <AIIcon />
              </IconButton>
            </Tooltip> */}

            {/* TEMPORARILY HIDDEN: Intelligence Icon */}
            {/* <Tooltip title="Intelligence">
              <IconButton
                onClick={() => navigate('/intelligence')}
                sx={{
                  color: location.pathname === '/intelligence' ? 'primary.main' : 'text.primary',
                  '&:hover': {
                    bgcolor: theme.palette.mode === 'dark' ? alpha('#fff', 0.12) : alpha('#000', 0.08),
                  },
                }}
              >
                <IntelligenceIcon />
              </IconButton>
            </Tooltip> */}
            
            {/* Search Bar/Button */}
            {!isMobile ? (
              <Box
                onClick={() => setSearchModalOpen(true)}
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 1,
                  px: 2,
                  py: 0.75,
                  borderRadius: 2,
                  border: 2,
                  borderColor: 'divider',
                  bgcolor: 'background.default',
                  cursor: 'pointer',
                  minWidth: 180,
                  transition: 'all 0.2s',
                  '&:hover': {
                    borderColor: 'primary.main',
                    bgcolor: alpha('#10b981', 0.05),
                  },
                }}
              >
                <SearchIcon sx={{ color: 'text.secondary', fontSize: 20 }} />
                <Typography variant="body2" sx={{ color: 'text.secondary', flexGrow: 1 }}>
                  Search...
                </Typography>
                <Typography variant="caption" sx={{
                  color: 'text.secondary',
                  bgcolor: alpha('#000', 0.05),
                  px: 0.75,
                  py: 0.25,
                  borderRadius: 0.5,
                  fontWeight: 500
                }}>
                  ⌘K
                </Typography>
              </Box>
            ) : (
              <Tooltip title="Search (⌘K or Ctrl+K)">
                <IconButton
                  onClick={() => setSearchModalOpen(true)}
                  size={isExtraSmall ? 'small' : isVerySmall ? 'small' : 'medium'}
                  sx={{
                    p: isExtraSmall ? 0.5 : undefined,
                    minWidth: 'unset'
                  }}
                >
                  <SearchIcon sx={{ fontSize: isExtraSmall ? 20 : 24 }} />
                </IconButton>
              </Tooltip>
            )}

            {/* Profile - Desktop only */}
            {!isMobile && (
              <Box
                onClick={handleProfileMenuOpen}
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 1,
                  cursor: 'pointer',
                  p: 0.5,
                  borderRadius: 1,
                  '&:hover': {
                    bgcolor: alpha('#fff', 0.05),
                  },
                }}
              >
                <Avatar
                  sx={{
                    width: 32,
                    height: 32,
                    fontSize: '0.875rem',
                    bgcolor: 'primary.main',
                  }}
                >
                  {user?.email?.[0]?.toUpperCase()}
                </Avatar>
                <ArrowDownIcon fontSize="small" sx={{ color: 'text.secondary' }} />
              </Box>
            )}

            {/* Mobile menu button */}
            {isMobile && (
              <IconButton
                onClick={() => setMobileDrawerOpen(true)}
                size={isExtraSmall ? 'small' : isVerySmall ? 'small' : 'medium'}
                sx={{ 
                  ml: { xs: 0.25, sm: 0.5 },
                  p: isExtraSmall ? 0.5 : undefined,
                  minWidth: 'unset'
                }}
              >
                <MenuIcon sx={{ fontSize: isExtraSmall ? 20 : 24 }} />
              </IconButton>
            )}
          </Box>
        </Toolbar>
      </AppBar>

      {/* Mobile Drawer */}
      <Drawer
        anchor="right"
        open={mobileDrawerOpen}
        onClose={() => setMobileDrawerOpen(false)}
        transitionDuration={0}
      >
        {mobileDrawerContent}
      </Drawer>

      {/* Main Content */}
      <Box
        id="main-scroll-container"
        component="main"
        sx={{
          flexGrow: 1,
          overflow: 'auto',
          pt: 8, // Account for AppBar height
          bgcolor: 'background.default',
          display: 'flex',
          flexDirection: 'column',
        }}
      >
        <Box sx={{
          p: 0,
          px: isFullBleed ? 0 : { xs: 2, sm: 2, md: 3 },
          pb: isFullBleed ? 0 : { xs: 2, sm: 2, md: 3 },
          width: '100%',
          height: isFullBleed ? '100%' : 'auto',
          display: isFullBleed ? 'flex' : 'block',
          '& > *': {
            width: '100%',
            ...(isFullBleed && { height: '100%' })
          }
        }}>
          {/* Navigation Elements */}
          {location.pathname !== '/' && !isFullBleed && (
            <Box sx={{ pt: { xs: 2, sm: 2, md: 3 }, pb: 1 }}>
              <AppBreadcrumbs />
            </Box>
          )}
          <Outlet />
        </Box>
      </Box>

      {/* Profile Menu */}
      <Menu
        anchorEl={profileAnchorEl}
        open={Boolean(profileAnchorEl)}
        onClose={handleProfileMenuClose}
        transitionDuration={0}
        PaperProps={{
          sx: {
            mt: 1,
            minWidth: 200,
          },
        }}
      >
        <Box sx={{ px: 2, py: 1.5 }}>
          <Typography variant="body2" sx={{ fontWeight: 500 }}>
            {user?.email}
          </Typography>
          <Typography variant="caption" sx={{ color: 'text.secondary' }}>
            {currentOrganization?.name}
          </Typography>
        </Box>
        <Divider />

        {/* Theme Toggle */}
        <ListItem sx={{ px: 2, py: 1 }}>
          <ListItemIcon sx={{ minWidth: 36 }}>
            {themeMode === 'dark' ? <DarkModeIcon fontSize="small" /> : <LightModeIcon fontSize="small" />}
          </ListItemIcon>
          <ListItemText
            primary="Dark Mode"
            primaryTypographyProps={{ fontSize: '0.875rem', fontWeight: 500 }}
          />
          <Switch
            edge="end"
            checked={themeMode === 'dark'}
            onChange={() => {
              toggleTheme();
              handleProfileMenuClose();
            }}
            size="small"
          />
        </ListItem>

        {/* Layout Toggle */}
        <ListItem sx={{ px: 2, py: 1 }}>
          <ListItemIcon sx={{ minWidth: 36 }}>
            {navigationLayout === 'top' ? <TopNavIcon fontSize="small" /> : <SidebarIcon fontSize="small" />}
          </ListItemIcon>
          <ListItemText
            primary="Top Navigation"
            primaryTypographyProps={{ fontSize: '0.875rem', fontWeight: 500 }}
          />
          <Switch
            edge="end"
            checked={navigationLayout === 'top'}
            onChange={handleLayoutToggle}
            size="small"
          />
        </ListItem>

        <Divider />
        <MenuItem onClick={() => {
          handleProfileMenuClose();
          navigate('/profile');
        }} sx={{ fontWeight: 500 }}>
          <ListItemIcon>
            <PersonIcon fontSize="small" />
          </ListItemIcon>
          Your Profile
        </MenuItem>
        <Divider />
        <MenuItem onClick={() => {
          handleProfileMenuClose();
          navigate('/settings');
        }} sx={{ fontWeight: 500 }}>
          <ListItemIcon>
            <SettingsIcon fontSize="small" />
          </ListItemIcon>
          General Settings
        </MenuItem>
        <MenuItem onClick={() => {
          handleProfileMenuClose();
          navigate('/admin/backups');
        }} sx={{ fontWeight: 500 }}>
          <ListItemIcon>
            <BackupIcon fontSize="small" />
          </ListItemIcon>
          Database Backups
        </MenuItem>
        <Divider />
        <MenuItem onClick={() => {
          handleProfileMenuClose();
          navigate('/subscription');
        }} sx={{ fontWeight: 500 }}>
          <ListItemIcon>
            <SubscriptionIcon fontSize="small" />
          </ListItemIcon>
          Subscription
        </MenuItem>
        <MenuItem onClick={() => {
          handleProfileMenuClose();
          navigate('/dealer-licences');
        }} sx={{ fontWeight: 500 }}>
          <ListItemIcon>
            <LicenceIcon fontSize="small" />
          </ListItemIcon>
          Dealer Licences
        </MenuItem>
        <MenuItem onClick={() => {
          handleProfileMenuClose();
          navigate('/bank-accounts');
        }} sx={{ fontWeight: 500 }}>
          <ListItemIcon>
            <BankIcon fontSize="small" />
          </ListItemIcon>
          Bank Accounts
        </MenuItem>
        <MenuItem onClick={() => {
          handleProfileMenuClose();
          navigate('/contacts');
        }} sx={{ fontWeight: 500 }}>
          <ListItemIcon>
            <ContactIcon fontSize="small" />
          </ListItemIcon>
          Contacts
        </MenuItem>
        <Divider />
        <MenuItem onClick={() => {
          handleProfileMenuClose();
          navigate('/help');
        }} sx={{ fontWeight: 500 }}>
          <ListItemIcon>
            <HelpIcon fontSize="small" />
          </ListItemIcon>
          Help & Support
        </MenuItem>
        <Divider />
        <MenuItem onClick={handleLogout} sx={{ fontWeight: 500 }}>
          <ListItemIcon>
            <LogoutIcon fontSize="small" />
          </ListItemIcon>
          Sign out
        </MenuItem>
      </Menu>

      {/* Global Search Modal */}
      <GlobalSearchModal open={searchModalOpen} onClose={() => setSearchModalOpen(false)} />

      {/* Quick Add Menu */}
      <Menu
        anchorEl={quickAddAnchorEl}
        open={Boolean(quickAddAnchorEl)}
        onClose={handleQuickAddMenuClose}
        transitionDuration={0}
        PaperProps={{
          sx: {
            mt: 1,
            minWidth: 280,
            bgcolor: 'background.paper',
            border: 1,
            borderColor: 'divider',
            boxShadow: 'none',
          },
        }}
      >
        {/* Quick Actions */}
        <Box sx={{ px: 2, py: 1, bgcolor: alpha('#10b981', 0.05) }}>
          <Typography variant="caption" sx={{ fontWeight: 600, color: 'primary.main', textTransform: 'uppercase' }}>
            Quick Create
          </Typography>
        </Box>
        <MenuItem onClick={() => handleQuickAddAction('/inventory/add')} sx={{ py: 1.5 }}>
          <ListItemIcon>
            <CarIcon fontSize="small" sx={{ color: 'primary.main' }} />
          </ListItemIcon>
          <Box>
            <Typography variant="body2" sx={{ fontWeight: 500 }}>Vehicle</Typography>
            <Typography variant="caption" sx={{ color: 'text.secondary' }}>Add to stock</Typography>
          </Box>
        </MenuItem>
        <Divider sx={{ opacity: 0.5 }} />
        <MenuItem onClick={() => handleQuickAddAction('/tasks')} sx={{ py: 1.5 }}>
          <ListItemIcon>
            <TaskIcon fontSize="small" sx={{ color: 'primary.main' }} />
          </ListItemIcon>
          <Box>
            <Typography variant="body2" sx={{ fontWeight: 500 }}>Task</Typography>
            <Typography variant="caption" sx={{ color: 'text.secondary' }}>Create task & set reminder</Typography>
          </Box>
        </MenuItem>
        <Divider sx={{ opacity: 0.5 }} />
        <MenuItem onClick={() => handleQuickAddAction('/customers/add')} sx={{ py: 1.5 }}>
          <ListItemIcon>
            <PersonAddIcon fontSize="small" sx={{ color: 'primary.main' }} />
          </ListItemIcon>
          <Box>
            <Typography variant="body2" sx={{ fontWeight: 500 }}>Contact</Typography>
            <Typography variant="caption" sx={{ color: 'text.secondary' }}>Customer, supplier, finance co. or dealer</Typography>
          </Box>
        </MenuItem>
        <Divider sx={{ opacity: 0.5 }} />
        <MenuItem onClick={() => handleQuickAddAction('/quote')} sx={{ py: 1.5 }}>
          <ListItemIcon>
            <QuoteIcon fontSize="small" sx={{ color: 'primary.main' }} />
          </ListItemIcon>
          <Box>
            <Typography variant="body2" sx={{ fontWeight: 500 }}>Proposal</Typography>
            <Typography variant="caption" sx={{ color: 'text.secondary' }}>Send pricing to customer</Typography>
          </Box>
        </MenuItem>
        <Divider sx={{ opacity: 0.5 }} />
        <MenuItem onClick={() => handleQuickAddAction('/sales/add')} sx={{ py: 1.5 }}>
          <ListItemIcon>
            <HandshakeIcon fontSize="small" sx={{ color: 'primary.main' }} />
          </ListItemIcon>
          <Box>
            <Typography variant="body2" sx={{ fontWeight: 500 }}>Sale</Typography>
            <Typography variant="caption" sx={{ color: 'text.secondary' }}>Create a deal & close sale</Typography>
          </Box>
        </MenuItem>
        <Divider sx={{ opacity: 0.5 }} />
        <MenuItem onClick={() => handleQuickAddAction('/purchase-orders/new')} sx={{ py: 1.5 }}>
          <ListItemIcon>
            <ReceiptLong fontSize="small" sx={{ color: 'primary.main' }} />
          </ListItemIcon>
          <Box>
            <Typography variant="body2" sx={{ fontWeight: 500 }}>Purchase Order</Typography>
            <Typography variant="caption" sx={{ color: 'text.secondary' }}>Order from supplier</Typography>
          </Box>
        </MenuItem>
        <Divider sx={{ opacity: 0.5 }} />
        <MenuItem onClick={() => { handleQuickAddMenuClose(); setQuickExpenseOpen(true); }} sx={{ py: 1.5 }}>
          <ListItemIcon>
            <MoneyIcon fontSize="small" sx={{ color: 'primary.main' }} />
          </ListItemIcon>
          <Box>
            <Typography variant="body2" sx={{ fontWeight: 500 }}>Expense</Typography>
            <Typography variant="caption" sx={{ color: 'text.secondary' }}>Vehicle or business expense</Typography>
          </Box>
        </MenuItem>
      </Menu>

      {/* Add Customer Modal */}
      <AddCustomerModal
        open={addCustomerModalOpen}
        onClose={() => setAddCustomerModalOpen(false)}
        onSuccess={(customerId) => {
          navigate(`/customers/${customerId}`);
        }}
      />

      <QuickAddExpenseModal
        open={quickExpenseOpen}
        onClose={() => setQuickExpenseOpen(false)}
      />

      {/* Service Flow Dialog for Workshop */}
      <ServiceFlowDialog
        open={serviceFlowOpen}
        onClose={() => setServiceFlowOpen(false)}
        onSuccess={() => {
          setServiceFlowOpen(false);
          navigate('/workshop');
        }}
      />

      {/* Sale/Proposal modals removed — Quick Add menu now navigates to /sales/new?mode=sale|quote */}
    </Box>
  );
}